'use client';
/**
 * Cmd/Ctrl+K — единая точка входа: сначала команды и экраны, потом данные.
 *
 * До редизайна палитра искала только сущности (`/search?q=`), а перейти в
 * раздел через неё было нельзя. После сокращения сайдбара до 12 областей
 * это стало обязательным: то, что не на виду, должно находиться за две
 * секунды набором.
 *
 * Порядок выдачи: Commands → Pages → Data. Разрушительные действия отсюда
 * не выполняются: команда только открывает нужный экран с готовой формой.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Search, CornerDownLeft, ArrowRight, Plus } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { api } from '@/lib/api';
import { useT } from '@/lib/useT';
import { cx, Spinner } from '@/components/ui';
import { flattenNav, type Role } from '@/lib/navigation';

interface Hit {
  id: string;
  group: 'command' | 'page' | 'data';
  label: string;
  hint?: string;
  href: string;
  icon?: LucideIcon;
}

/** Безопасные команды: каждая ведёт на экран, ничего не меняет молча. */
const COMMANDS: { labelKey: string; fallback: string; href: string; keywords: string[] }[] = [
  { labelKey: 'ord_from_template', fallback: 'New order from template', href: '/dashboard/orders/templates', keywords: ['new', 'создать', 'заказ', 'auftrag', 'template'] },
  { labelKey: 'cmd_plan_route',  fallback: 'Plan a route',   href: '/dashboard/tour-planner',        keywords: ['route', 'маршрут', 'tour'] },
  { labelKey: 'cmd_add_driver',  fallback: 'Drivers',        href: '/dashboard/drivers',             keywords: ['driver', 'водитель', 'fahrer'] },
  { labelKey: 'cmd_add_vehicle', fallback: 'Vehicles',       href: '/dashboard/fleet',               keywords: ['vehicle', 'truck', 'машина'] },
  { labelKey: 'cmd_import',      fallback: 'Import data',    href: '/dashboard/import',              keywords: ['import', 'excel', 'csv', 'импорт'] },
  { labelKey: 'cmd_integrations',fallback: 'Open integrations', href: '/dashboard/settings?tab=integrations', keywords: ['api', 'webhook', 'интеграции'] },
];

export default function CommandPalette({ role = 'admin' }: { role?: Role }) {
  const t = useT();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState(0);
  const [data, setData] = useState<Hit[]>([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounce = useRef<number | null>(null);

  const label = (key: string, fallback: string) => {
    const v = t(key);
    return !v || v === key ? fallback : v;
  };

  /* ── Открытие / закрытие ─────────────────────────────────────── */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) { setQ(''); setData([]); setCursor(0); setTimeout(() => inputRef.current?.focus(), 20); }
  }, [open]);

  /* ── Локальные результаты: команды и экраны ──────────────────── */
  const local = useMemo<Hit[]>(() => {
    const needle = q.trim().toLowerCase();
    const pages = flattenNav(role).map<Hit>((leaf) => ({
      id: `page:${leaf.href}`,
      group: 'page',
      label: label(leaf.labelKey, leaf.fallback),
      hint: leaf.href.replace('/dashboard', '') || '/',
      href: leaf.href,
      icon: leaf.icon,
    }));
    const commands = COMMANDS.map<Hit>((c) => ({
      id: `cmd:${c.href}`,
      group: 'command',
      label: label(c.labelKey, c.fallback),
      href: c.href,
      icon: Plus,
    }));

    if (!needle) return [...commands.slice(0, 4), ...pages.slice(0, 8)];

    const match = (text: string, extra: string[] = []) =>
      text.toLowerCase().includes(needle) || extra.some((k) => k.toLowerCase().includes(needle));

    const cmdHits = commands.filter((c, i) => match(c.label, COMMANDS[i].keywords));
    const navLeaves = flattenNav(role);
    const pageHits = pages.filter((p, i) => match(p.label, [...(navLeaves[i].keywords ?? []), p.href]));
    return [...cmdHits, ...pageHits];
  }, [q, role, t]);

  /* ── Серверный поиск по сущностям ────────────────────────────── */
  const searchData = useCallback(async (query: string) => {
    if (query.trim().length < 2) { setData([]); return; }
    setBusy(true);
    try {
      const r: any = await api.get(`/search?q=${encodeURIComponent(query)}`);
      const hits: Hit[] = (r?.hits ?? r ?? []).slice(0, 8).map((h: any, i: number) => ({
        id: `data:${h.id ?? i}`,
        group: 'data',
        label: h.title ?? h.label ?? h.name ?? String(h.id ?? ''),
        hint: h.subtitle ?? h.type,
        href: h.path ?? h.href ?? '#',
      }));
      setData(hits);
    } catch {
      setData([]);   // поиск данных не обязан работать, чтобы палитра была полезна
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (debounce.current) window.clearTimeout(debounce.current);
    debounce.current = window.setTimeout(() => searchData(q), 220);
    return () => { if (debounce.current) window.clearTimeout(debounce.current); };
  }, [q, searchData]);

  const results = useMemo(() => [...local, ...data], [local, data]);
  useEffect(() => { setCursor(0); }, [q]);

  const go = (hit: Hit) => {
    setOpen(false);
    if (hit.href && hit.href !== '#') router.push(hit.href);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(c + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
    else if (e.key === 'Enter' && results[cursor]) { e.preventDefault(); go(results[cursor]); }
  };

  /* ── Триггер в топбаре ───────────────────────────────────────── */
  const trigger = (
    <button
      onClick={() => setOpen(true)}
      className={cx(
        'flex items-center gap-2 h-8 w-full max-w-[320px] px-2.5 text-left',
        'bg-[var(--surface-sunken)] border border-[var(--border)] rounded-[var(--radius-sm)]',
        'text-[12.5px] text-[var(--text-muted)] hover:border-[var(--border-strong)] transition-colors',
        'focus-visible:outline-none focus-visible:shadow-[var(--focus-ring)]',
      )}
    >
      <Search size={13} />
      <span className="truncate">{label('search_ph', 'Search or jump to…')}</span>
      <kbd className="ml-auto font-mono text-[10px] px-1 py-0.5 rounded-[var(--radius-xs)] border border-[var(--border)] text-[var(--text-muted)]">⌘K</kbd>
    </button>
  );

  if (!open) return trigger;

  const GROUP_TITLE: Record<Hit['group'], string> = {
    command: label('cmd_group_commands', 'Commands'),
    page: label('cmd_group_pages', 'Pages'),
    data: label('cmd_group_data', 'Results'),
  };

  return (
    <>
      {trigger}
      <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh] px-4"
           onClick={() => setOpen(false)}>
        <div className="absolute inset-0 bg-black/40" aria-hidden />
        <div
          role="dialog" aria-modal="true" aria-label="Command palette"
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-[600px] bg-[var(--surface-raised)] border border-[var(--border-strong)] rounded-[var(--radius-lg)] shadow-[var(--shadow-md)] overflow-hidden"
        >
          <div className="flex items-center gap-2.5 px-3.5 h-12 border-b border-[var(--border)]">
            <Search size={15} className="text-[var(--text-muted)] shrink-0" />
            <input
              ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKeyDown}
              placeholder={label('cmd_placeholder', 'Search pages, orders, drivers, vehicles…')}
              className="flex-1 bg-transparent border-0 outline-none text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]"
            />
            {busy && <Spinner size={13} />}
          </div>

          <div className="max-h-[52vh] overflow-y-auto py-1.5">
            {results.length === 0 && (
              <p className="px-3.5 py-8 text-center text-[13px] text-[var(--text-muted)]">
                {label('cmd_nothing', 'Nothing matches')} “{q}”
              </p>
            )}
            {(['command', 'page', 'data'] as const).map((group) => {
              const items = results.filter((r) => r.group === group);
              if (!items.length) return null;
              return (
                <div key={group}>
                  <p className="px-3.5 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)]">
                    {GROUP_TITLE[group]}
                  </p>
                  {items.map((hit) => {
                    const idx = results.indexOf(hit);
                    const on = idx === cursor;
                    const Icon = hit.icon ?? ArrowRight;
                    return (
                      <button
                        key={hit.id}
                        onMouseEnter={() => setCursor(idx)}
                        onClick={() => go(hit)}
                        className={cx(
                          'flex items-center gap-2.5 w-full h-9 px-3.5 text-left transition-colors',
                          on ? 'bg-[var(--brand-subtle)]' : 'hover:bg-[var(--surface-hover)]',
                        )}
                      >
                        <Icon size={14} className={cx('shrink-0', on ? 'text-[var(--brand)]' : 'text-[var(--text-muted)]')} />
                        <span className="text-[13px] text-[var(--text-primary)] truncate">{hit.label}</span>
                        {hit.hint && <span className="ml-auto text-[11px] font-mono text-[var(--text-muted)] truncate max-w-[45%]">{hit.hint}</span>}
                        {on && <CornerDownLeft size={12} className="text-[var(--text-muted)] shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>

          <div className="flex items-center gap-3 px-3.5 h-8 border-t border-[var(--border)] text-[10.5px] text-[var(--text-muted)]">
            <span>↑↓ navigate</span><span>↵ open</span><span>esc close</span>
          </div>
        </div>
      </div>
    </>
  );
}
